import cn from "classnames"
import createFileList from "create-file-list"
import is from "is_js"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Input, InputProps } from "reactstrap"
import {
    DataType,
    StandardButton,
    TClearError,
    TRegister,
    TSetError,
    TSetValue,
    TTriggerValidation,
    getKeys,
    valFuncFileQuantitiesAndProperties,
} from "swiipe.portal.shared"
import UploadedFilesOverview from "../../overview/UploadedFilesOverview"
import "./FileInput.scss"

export const megabyteInBytes = 1048576
interface IFileInputProps<T> extends InputProps {
    containerClass?: string
    hideUploadArea?: boolean
    name: Extract<keyof T, string>
    accept: string
    multiple?: boolean
    currentFilesAmount?: number
    minFilesAmount?: number
    maxFilesAmount?: number
    requiredWidth?: number
    requiredHeight?: number
    maxFileSizeBytes?: number
    requiredErrorMessage?: string
    requiredTypes?: string[]
    wrongTypeFilesErrorMessage?: string
    slimButton?: boolean
    setError: TSetError<T>
    clearError: TClearError<T>
    triggerValidation: TTriggerValidation<T>
    getValues: (name: string) => any
    setValue: TSetValue<T>
    register: TRegister
    registerClearing?: { register: (name: string, clear: () => void) => void }
    showUnsavedWarning?: boolean
}

const FileInput = <T extends DataType>({
    containerClass,
    name,
    accept,
    multiple,
    currentFilesAmount,
    minFilesAmount,
    maxFilesAmount,
    requiredWidth,
    requiredHeight,
    maxFileSizeBytes,
    requiredErrorMessage,
    requiredTypes,
    wrongTypeFilesErrorMessage,
    slimButton,
    setError,
    clearError,
    triggerValidation,
    setValue,
    getValues,
    register,
    hideUploadArea,
    registerClearing,
    showUnsavedWarning,
    ...otherProps
}: IFileInputProps<T>) => {
    const { t } = useTranslation()

    const maximumFilesAmount = maxFilesAmount ? maxFilesAmount : 1
    const calcedMinFilesAmount = Math.max(0, (minFilesAmount ?? 0) - (currentFilesAmount ?? 0))
    const calcedMaxFilesAmount = Math.max(0, (maximumFilesAmount ?? 0) - (currentFilesAmount ?? 0))

    const [uploadedFilesList, setUploadedFilesList] = useState({} as FileList)

    const [showFilesCountError, setShowFilesCountError] = useState(false)
    const [showFileSizeError, setShowFileSizeError] = useState(false)

    const fileListRef = useRef<HTMLDivElement | null | undefined>()

    useEffect(() => {
        const filesKeys = getKeys(uploadedFilesList)

        if (!filesKeys.length) {
            return
        }

        if (maxFileSizeBytes) {
            const hasToLargeFiles = filesKeys.find((key) => uploadedFilesList[key].size > maxFileSizeBytes)
            setShowFileSizeError(!!hasToLargeFiles)
        }

        filesKeys.length <= calcedMaxFilesAmount ? setShowFilesCountError(false) : setShowFilesCountError(true)
    }, [uploadedFilesList])

    useEffect(() => {
        if (registerClearing) {
            registerClearing.register(name, () => {
                for (let i = uploadedFilesList.length - 1; i >= 0; i--) {
                    removeFile(i)
                }
            })
        }
    }, [registerClearing, uploadedFilesList])

    const updateUploadedFiles = () => {
        const newFiles: FileList = getValues(name)

        //Create file list is not supported by IE and Safari
        //So add only new files
        if (is.safari() || is.ie()) {
            setUploadedFilesList(newFiles)
            setValue(name, newFiles)

            return
        }

        const filesToAdd: File[] = getKeys(newFiles).map((key) => newFiles[key])
        const previousFiles: File[] = getKeys(uploadedFilesList).map((key) => uploadedFilesList[key])

        const allFiles = createFileList(previousFiles, filesToAdd)

        setUploadedFilesList(allFiles)

        setValue(name, allFiles)
    }

    const removeFile = (index: number) => {
        //Create file list is not supported by IE and Safari
        //So remove all files
        if (is.safari() || is.ie()) {
            setUploadedFilesList({} as FileList)
            setValue(name, null)

            return
        }

        const files = getKeys(uploadedFilesList).map((key) => uploadedFilesList[key])
        const filesFiltered = files.filter((f, i) => (i === index ? false : true))

        const fileList = createFileList(filesFiltered)
        setUploadedFilesList(fileList)
        setValue(name, fileList)

        triggerValidation(name)
    }

    if (maxFilesAmount && calcedMaxFilesAmount <= 0) {
        return null
    }

    return (
        <>
            <div className={cn("swiipe-file-upload", "mt-3", containerClass)}>
                {!hideUploadArea && (
                    <span>
                        {t("fileupload.dropfiles")}
                        {maxFileSizeBytes && (
                            <>{t("fileupload.dropfilesmaxsize", { fileSize: maxFileSizeBytes / megabyteInBytes })}</>
                        )}
                    </span>
                )}
                <StandardButton dark noBorder noBorderRadius className={cn("upload-btn", { "slim-btn": slimButton })}>
                    {t("fileupload.upload")}
                </StandardButton>
                <Input
                    type="file"
                    name={name}
                    multiple={multiple}
                    accept={accept}
                    onChange={() => {
                        updateUploadedFiles()
                    }}
                    innerRef={register(
                        valFuncFileQuantitiesAndProperties(
                            requiredErrorMessage || "",
                            calcedMinFilesAmount,
                            calcedMaxFilesAmount,
                            maxFileSizeBytes,
                            wrongTypeFilesErrorMessage,
                            requiredTypes,
                            requiredHeight,
                            requiredWidth
                        )
                    )}
                    {...otherProps}
                ></Input>
            </div>
            <div
                className={cn("swiipe-files-list", {
                    "d-none": getKeys(uploadedFilesList).length === 0,
                })}
                ref={(ref) => (fileListRef.current = ref)}
            >
                <UploadedFilesOverview
                    files={uploadedFilesList}
                    onRemoveFile={(i) => {
                        removeFile(i)
                    }}
                    showUnsavedWarning={showUnsavedWarning}
                />
                {showFilesCountError && (
                    <span className="error">{t("fileupload.dropatleast", { filesAmount: calcedMaxFilesAmount })}</span>
                )}
                {showFileSizeError && maxFileSizeBytes && (
                    <span className="error">
                        {t("fileupload.errorfilesize", { fileSize: maxFileSizeBytes / megabyteInBytes })}
                    </span>
                )}
            </div>
        </>
    )
}

export default FileInput
